import React, { useState } from 'react';
import { NavLink } from '@mantine/core';
import type { MobileMenuNavItem, MobileMenuTopLevelNode } from 'common-ui';
import { CmsContentSection, Icon, inspectorMode } from 'common-ui';
import type { DLClickEventHandlerProps } from 'tsconfig/types';
import { globals } from '../../../../utils/globals';
import RenderLevel3Menu from './RenderLevel3Menu';

type Props = {
  menuItems: MobileMenuTopLevelNode;
  onBack: () => void;
  dlClickEventHandler: DLClickEventHandlerProps;
};

const getNextExpandedMenuIds = (expandedMenuIds: string[], menuId: string, isExpanded: boolean): string[] => {
  if (isExpanded) {
    return expandedMenuIds.filter(existingMenuId => existingMenuId !== menuId);
  }
  return [...expandedMenuIds, menuId];
};

const RenderLevel2Menu = ({ menuItems, onBack, dlClickEventHandler }: Props): React.JSX.Element | null => {
  const [expandedMenuIds, setExpandedMenuIds] = useState<string[]>([]);

  if (!menuItems) {
    return null;
  }

  const level2Menu = menuItems?.children?.map(menuItem => {
    if (menuItem.contentType === 'navigation') {
      const navItem = menuItem as MobileMenuNavItem;
      const hasChildren = Boolean(navItem.children?.length);
      const hasValidUrl = navItem.url && navItem.url !== '#' && navItem.url !== '';

      // If has children, render as expandable NavLink
      if (hasChildren) {
        const isExpanded = expandedMenuIds.includes(navItem.id);
        return (
          <NavLink
            key={navItem.id}
            className="level2Link"
            label={navItem.title}
            rightSection={<Icon kind="chevron-down" size="m" />}
            opened={isExpanded}
            onClick={() => {
              dlClickEventHandler(`${menuItems.title}:${navItem.title}`, undefined, isExpanded ? 'collapse' : 'expand');
              setExpandedMenuIds(getNextExpandedMenuIds(expandedMenuIds, navItem.id, isExpanded));
            }}
            {...inspectorMode(navItem.id, 'title')}
          >
            <RenderLevel3Menu
              level3MenuContent={navItem}
              level2MenuContentTitle={menuItems.title}
              dlClickEventHandler={dlClickEventHandler}
            />
          </NavLink>
        );
      }

      // If no children but has valid URL, render as navigation link
      if (hasValidUrl) {
        return (
          <NavLink
            key={navItem.id}
            component="a"
            href={navItem.url}
            className="level2Link"
            label={navItem.title}
            onClick={() => {
              dlClickEventHandler(`${menuItems.title}:${navItem.title}`, undefined, 'navigation');
            }}
            {...inspectorMode(navItem.id, 'title')}
          />
        );
      }

      // If no children and no valid URL, don't render
      return null;
    }

    return (
      <div className="navMenuContent" key={menuItem.id}>
        <CmsContentSection
          contentJsonList={[menuItem.contentItem]}
          dlClickEventHandler={(data: string) => {
            dlClickEventHandler(`${menuItems.title}:${data}`);
          }}
        />
      </div>
    );
  });

  return (
    <>
      <div className="navMenuBackLink">
        <button
          type="button"
          className="navMenuBackButton"
          onClick={() => {
            dlClickEventHandler(menuItems.title, undefined, 'collapse');
            onBack();
          }}
          aria-label="Go back to previous menu"
        >
          <Icon kind="back" size="xs" />
        </button>
        {menuItems?.url && globals.country === 'uk' ? (
          <a
            href={menuItems.url}
            className="navMenuBackLinkTitle"
            onClick={e => {
              e.stopPropagation();
              dlClickEventHandler(`${menuItems.title}`, undefined, 'navigation');
            }}
          >
            {menuItems?.title}
          </a>
        ) : (
          <span className="navMenuBackLinkTitle">{menuItems?.title}</span>
        )}
      </div>
      {level2Menu}
    </>
  );
};

export default RenderLevel2Menu;
